import React, { useEffect, useState } from "react";
import { MessageSquare, Copy as CopyIcon, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";

/**
 * Phase 22 — WhatsApp AI draft modal.
 *
 * Opens after a successful order/shipment status PATCH so the admin can send
 * a short cordial WhatsApp notification with one click. The AI paragraph is
 * editable before copying / opening wa.me.
 *
 * Props:
 *   kind      — "order" | "shipment" (controls which AI endpoint is called).
 *   entityId  — order_id or shipment_id (uuid).
 *   status    — new status string (e.g. "preparando").
 *   phone     — optional recipient phone (E.164, no plus). Used to build wa.me link.
 *   onClose   — close handler.
 */
export default function StatusMessageModal({ kind, entityId, status, phone, onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchDraft = async () => {
    setLoading(true);
    const t = toast.loading("IA redactando…");
    try {
      const body =
        kind === "order"
          ? { order_id: entityId, new_status: status }
          : { shipment_id: entityId, new_status: status };
      const path =
        kind === "order"
          ? "/admin/ai/orders/status-message"
          : "/admin/ai/shipments/status-message";
      const { data } = await api.post(path, body);
      setText(data.message || "");
      toast.success("Mensaje generado", { id: t });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout"
          ? "IA tardó demasiado"
          : "IA no disponible",
        { id: t },
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const cleanPhone = (phone || "").replace(/[^0-9]/g, "");
  const waHref = cleanPhone
    ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`
    : null;

  return (
    <div
      role="dialog"
      aria-label="Mensaje WhatsApp"
      data-testid="status-msg-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-emerald-600" />
            Mensaje WhatsApp — IA
          </h3>
          <button
            data-testid="status-msg-modal-close"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
          >
            <span aria-hidden>×</span>
          </button>
        </div>
        <p className="text-xs text-neutral-500">
          Borrador para notificar al cliente el cambio a estado «{status}».
          Puedes editarlo antes de enviarlo.
        </p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          data-testid="status-msg-modal-text"
          placeholder={loading ? "Generando…" : ""}
        />
        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-neutral-100">
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchDraft}
            disabled={loading}
            data-testid="status-msg-modal-regen"
            className="gap-1"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Regenerar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={copy}
            disabled={!text}
            data-testid="status-msg-modal-copy"
            className="gap-1"
          >
            <CopyIcon className="h-3 w-3" /> Copiar
          </Button>
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="status-msg-modal-wa"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold"
            >
              <Send className="h-3.5 w-3.5" /> Abrir WhatsApp
            </a>
          ) : (
            <Button size="sm" disabled data-testid="status-msg-modal-wa-disabled">
              Sin teléfono
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
